/**
 * 知识库 — 文档上传、解析、检索（真实 API 接入）
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  uploadDocument,
  fetchKnowledgeDocuments,
  searchKnowledge,
  deleteKnowledgeDocument,
  KnowledgeDocumentDTO,
} from '../../api/knowledge';

export default function KnowledgePage() {
  const [docs, setDocs] = useState<KnowledgeDocumentDTO[]>([]);
  const [search, setSearch] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load documents on mount
  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const result = await fetchKnowledgeDocuments({ page: 0, size: 50 });
      setDocs(result.items || []);
    } catch {
      // API not available yet, show empty
      setDocs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file?: File) => {
    if (!file) return;
    setUploading(true);
    try {
      const doc = await uploadDocument(file);
      setDocs((prev) => [doc, ...prev]);
    } catch (e) {
      alert('上传失败，请检查网络连接');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleUpload(file);
  };

  const handleSearch = async () => {
    if (!search.trim()) {
      loadDocuments();
      return;
    }
    setLoading(true);
    try {
      const result = await searchKnowledge(search, { page: 0, size: 20 });
      setDocs(result.items || []);
    } catch {
      // Fallback: filter locally
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (docId: string) => {
    if (!confirm('确定要删除这个文档吗？')) return;
    try {
      await deleteKnowledgeDocument(docId);
      setDocs((prev) => prev.filter((d) => d.docId !== docId));
    } catch {
      alert('删除失败');
    }
  };

  const statusMap: Record<string, { label: string; color: string }> = {
    READY: { label: '已就绪', color: '#34d399' },
    PROCESSING: { label: '处理中', color: '#fbbf24' },
    ERROR: { label: '失败', color: '#f87171' },
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>📚 知识库</h1>
        <p style={styles.subtitle}>上传文档，让 AI Agent 拥有领域知识</p>
      </div>

      {/* Upload Area */}
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        accept=".pdf,.docx,.doc,.md,.txt,.html"
        onChange={handleFileSelect}
      />
      <div
        style={{ ...styles.uploadArea, ...(dragOver ? styles.uploadAreaHover : {}) }}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <span style={styles.uploadEmoji}>{uploading ? '⏳' : '📤'}</span>
        <span style={styles.uploadText}>
          {uploading ? '上传中...' : '拖拽文件到此处，或点击上传'}
        </span>
        <span style={styles.uploadHint}>支持 PDF、Word、Markdown、TXT，单文件最大 50MB</span>
      </div>

      {/* Search */}
      <div style={styles.searchRow}>
        <input
          style={styles.search}
          placeholder="🔍 搜索知识库内容..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
        />
        <button style={styles.searchBtn} onClick={handleSearch}>搜索</button>
      </div>

      {/* Stats */}
      <div style={styles.stats}>
        <div style={styles.stat}>
          <span style={styles.statVal}>{docs.length}</span>
          <span style={styles.statLbl}>文档总数</span>
        </div>
        <div style={styles.stat}>
          <span style={styles.statVal}>{docs.reduce((s, d) => s + (d.chunkCount || 0), 0)}</span>
          <span style={styles.statLbl}>知识片段</span>
        </div>
        <div style={styles.stat}>
          <span style={styles.statVal}>{docs.filter((d) => d.status === 'READY').length}</span>
          <span style={styles.statLbl}>已就绪</span>
        </div>
        <div style={styles.stat}>
          <span style={styles.statVal}>{docs.reduce((s, d) => s + (d.totalTokens || 0), 0)}</span>
          <span style={styles.statLbl}>总 Tokens</span>
        </div>
      </div>

      {/* Document List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>加载中...</div>
      ) : docs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
          暂无文档，上传第一个文档开始使用知识库
        </div>
      ) : (
        <div style={styles.docList}>
          {docs.map((doc) => (
            <div key={doc.docId} style={styles.docItem}>
              <span style={styles.docEmoji}>
                {doc.fileType === 'PDF' ? '📄' : doc.fileType === 'MD' ? '📝' : '📃'}
              </span>
              <div style={styles.docInfo}>
                <span style={styles.docName}>{doc.name}</span>
                <span style={styles.docMeta}>
                  {doc.fileType} · {formatSize(doc.fileSize || 0)} ·{' '}
                  {doc.chunkCount > 0 ? `${doc.chunkCount} 个片段` : '处理中...'}
                </span>
              </div>
              <span style={{ ...styles.docStatus, color: statusMap[doc.status]?.color || '#64748b' }}>
                {statusMap[doc.status]?.label || doc.status}
              </span>
              <button
                style={styles.deleteBtn}
                onClick={(e) => { e.stopPropagation(); handleDelete(doc.docId); }}
              >
                🗑️
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { maxWidth: 900, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 },
  header: {},
  title: { fontSize: 22, fontWeight: 700, margin: 0 },
  subtitle: { fontSize: 12, color: '#64748b', margin: '4px 0 0' },
  uploadArea: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
    padding: '28px 16px', borderRadius: 12, border: '2px dashed rgba(255,255,255,0.1)',
    background: 'rgba(255,255,255,0.02)', cursor: 'pointer', transition: 'all 0.2s',
  },
  uploadAreaHover: { borderColor: '#6366f1', background: 'rgba(99,102,241,0.05)' },
  uploadEmoji: { fontSize: 28 },
  uploadText: { fontSize: 13, fontWeight: 600 },
  uploadHint: { fontSize: 10, color: '#64748b' },
  searchRow: { display: 'flex', gap: 8 },
  search: {
    flex: 1, padding: '10px 14px', borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)',
    color: '#e2e8f0', fontSize: 13, outline: 'none',
  },
  searchBtn: {
    padding: '10px 20px', borderRadius: 8, border: 'none',
    background: '#6366f1', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  deleteBtn: {
    background: 'transparent', border: 'none', color: '#f87171', fontSize: 14,
    cursor: 'pointer', padding: '4px 8px', borderRadius: 4,
  },
  stats: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 },
  stat: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
    padding: '12px 8px', borderRadius: 10, background: 'rgba(255,255,255,0.025)',
    border: '1px solid rgba(255,255,255,0.06)',
  },
  statVal: { fontSize: 18, fontWeight: 700 },
  statLbl: { fontSize: 10, color: '#64748b' },
  docList: { display: 'flex', flexDirection: 'column', gap: 8 },
  docItem: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '12px 14px', borderRadius: 10, background: 'rgba(255,255,255,0.025)',
    border: '1px solid rgba(255,255,255,0.06)',
  },
  docEmoji: { fontSize: 20 },
  docInfo: { flex: 1, display: 'flex', flexDirection: 'column', gap: 2 },
  docName: { fontSize: 13, fontWeight: 600 },
  docMeta: { fontSize: 10, color: '#64748b' },
  docStatus: { fontSize: 11, fontWeight: 600 },
};
